/**
 * InAppNotificationContent
 * ========================
 * Renders the rich body of an admin-authored in-app notification: the Markdown
 * the admin wrote, plus its media attachments.
 *
 * 🔴 THE BODY IS MARKDOWN SOURCE, NOT HTML, AND MUST STAY THAT WAY.
 * The backend has no HTML sanitizer (see the header of
 * `backend/src/domain/constants/adminAnnouncementMarkdown.js`), so it deliberately
 * ships the SOURCE to this component instead of server-rendered markup. react-markdown
 * does not evaluate raw HTML unless `rehype-raw` is added — which is exactly why it is
 * not added here. Do NOT swap this for `dangerouslySetInnerHTML`, and do NOT add
 * `rehype-raw`: either one turns admin-authored text into a live injection surface in
 * every user's browser. This is the same rule AdminAnnouncementModal follows, arrived
 * at from the other direction (that one flattens to text; this one renders a
 * restricted Markdown subset).
 *
 * Links and images are additionally scheme-checked here rather than trusted from
 * the payload: the backend validates on write, but a row persisted before a
 * validation change still has to render safely.
 */
import React from 'react';
import Markdown from 'react-markdown';
import { Download, ExternalLink, FileText, Music, Video } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  InAppAnnouncement,
  InAppMediaItem,
  isInternalInAppActionUrl,
  isSafeInAppActionUrl,
  mediaTypeLabel,
} from '@/lib/notifications/inAppNotification';

/** http(s) only, for anything that becomes a `src`. */
const isSafeMediaUrl = (url: unknown): url is string =>
  typeof url === 'string' && /^https?:\/\//i.test(url.trim());

/**
 * Markdown element overrides.
 *
 * Two jobs: keep the output inside the app's type scale, and re-check every URL
 * the author supplied. An `href` that fails the scheme test renders as plain text
 * so the link text is not lost.
 */
const markdownComponents = {
  a: ({ href, children, ...props }: React.ComponentPropsWithoutRef<'a'> & { href?: string }) =>
    isSafeInAppActionUrl(href) ? (
      // `noopener,noreferrer` — the target page must not be able to reach back
      // into this one through `window.opener`.
      <a
        {...props}
        href={href}
        target={href.startsWith('/') ? undefined : '_blank'}
        rel="noopener noreferrer"
        className="text-blue-600 underline underline-offset-2 hover:text-blue-700"
      >
        {children}
      </a>
    ) : (
      <span>{children}</span>
    ),
  img: ({ src, alt }: React.ComponentPropsWithoutRef<'img'>) =>
    isSafeMediaUrl(src) ? (
      <img
        src={src}
        alt={alt || ''}
        loading="lazy"
        className="my-2 max-h-72 w-full rounded-lg object-cover"
        // A dead admin-supplied URL must not leave a torn-image icon in the body.
        onError={(event) => {
          event.currentTarget.style.display = 'none';
        }}
      />
    ) : null,
  // Tables/code blocks can be far wider than a bell dropdown; scroll them
  // inside their own box rather than letting the row scroll sideways.
  pre: ({ children, ...props }: React.ComponentPropsWithoutRef<'pre'>) => (
    <pre {...props} className="overflow-x-auto rounded-md bg-gray-100 p-3 text-xs">
      {children}
    </pre>
  ),
} as const;

const MEDIA_ICON: Record<InAppMediaItem['type'], React.ComponentType<{ className?: string }>> = {
  image: FileText,
  video: Video,
  audio: Music,
  document: FileText,
};

/** One attachment. Images and video render inline; audio gets a player; anything else is a link. */
const MediaAttachment: React.FC<{ item: InAppMediaItem }> = ({ item }) => {
  if (!isSafeMediaUrl(item.url)) return null;

  if (item.type === 'image') {
    return (
      <figure className="overflow-hidden rounded-lg border border-gray-100">
        <img
          src={item.url}
          alt={item.caption || item.fileName || ''}
          loading="lazy"
          className="max-h-72 w-full object-cover"
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />
        {item.caption ? (
          <figcaption className="px-3 py-2 text-xs text-muted-foreground">{item.caption}</figcaption>
        ) : null}
      </figure>
    );
  }

  if (item.type === 'video') {
    return (
      <figure className="overflow-hidden rounded-lg border border-gray-100">
        <video
          src={item.url}
          poster={item.thumbnailUrl || undefined}
          controls
          preload="metadata"
          className="max-h-72 w-full bg-black"
        />
        {item.caption ? (
          <figcaption className="px-3 py-2 text-xs text-muted-foreground">{item.caption}</figcaption>
        ) : null}
      </figure>
    );
  }

  if (item.type === 'audio') {
    return (
      <div className="rounded-lg border border-gray-100 p-3">
        <audio src={item.url} controls preload="metadata" className="w-full" />
        {item.caption ? <p className="mt-2 text-xs text-muted-foreground">{item.caption}</p> : null}
      </div>
    );
  }

  const Icon = MEDIA_ICON[item.type] || FileText;
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 rounded-lg border border-gray-100 p-3 transition-colors hover:bg-gray-50"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gray-100">
        <Icon className="h-4 w-4 text-gray-600" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">
          {item.fileName || item.caption || mediaTypeLabel(item.type)}
        </span>
        <span className="block truncate text-xs text-muted-foreground">{item.url}</span>
      </span>
      <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
    </a>
  );
};

export const InAppNotificationContent: React.FC<{
  announcement: InAppAnnouncement;
  className?: string;
  /** Compact mode drops the media gallery — used where space is tight (previews). */
  compact?: boolean;
  /**
   * Called when an action button pointing INSIDE the app is clicked, so the
   * host can navigate with the router (and close its dialog) instead of doing a
   * full page load. Without it an internal button falls back to a plain link,
   * which still works — it just reloads the app.
   */
  onNavigate?: (url: string) => void;
}> = ({ announcement, className, compact = false, onNavigate }) => {
  const { body, bodyFormat, media, actionButtons } = announcement;

  return (
    <div className={cn('space-y-3', className)} data-testid="in-app-notification-content">
      {body.trim() ? (
        bodyFormat === 'markdown' ? (
          <div className="prose prose-sm max-w-none break-words text-foreground prose-headings:mb-1 prose-headings:mt-3 prose-p:my-2">
            <Markdown components={markdownComponents}>{body}</Markdown>
          </div>
        ) : (
          <p className="whitespace-pre-wrap break-words text-sm text-foreground">{body}</p>
        )
      ) : null}

      {!compact && media.length > 0 ? (
        <div className="space-y-2" data-testid="in-app-notification-media">
          {media.map((item) => (
            <MediaAttachment key={item.url} item={item} />
          ))}
        </div>
      ) : null}

      {/* Action buttons — rendered even in compact mode: they are the point of
          the announcement when present, and dropping them in a preview would
          show the admin something different from what recipients get.
          `actionButtons` is optional on the type only for older callers that
          build an announcement literal by hand. */}
      {actionButtons?.length ? (
        <div className="flex flex-wrap gap-2 pt-1" data-testid="in-app-notification-actions">
          {actionButtons.map((button) => {
            const internal = isInternalInAppActionUrl(button.url);
            if (internal && onNavigate) {
              return (
                <Button
                  key={button.id}
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-[8px]"
                  onClick={() => onNavigate(button.url)}
                >
                  {button.label}
                </Button>
              );
            }
            return (
              <Button
                key={button.id}
                asChild
                size="sm"
                variant="outline"
                className="rounded-[8px]"
              >
                <a
                  href={button.url}
                  // `noopener` on every external link: without it the opened page
                  // gets a live `window.opener` handle back into this app.
                  {...(internal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                >
                  {button.label}
                  {!internal && <ExternalLink className="ml-1.5 h-3 w-3" />}
                </a>
              </Button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default InAppNotificationContent;
